import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtMs, fmtBytes, now } from '@/utils/format';

interface Props { autoRun: boolean; autoNonce: number; }

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

export function NetworkCapTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    connectionApi: false,
    effectiveType: '—',
    downlink: null as number | null,
    rtt: null as number | null,
    online: navigator.onLine,
    throughputMs: null as number | null,
    throughputBytes: null as number | null,
  });
  const autoDoneRef = useRef(false);

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    setMetrics({
      connectionApi: false, effectiveType: '—',
      downlink: null, rtt: null, online: navigator.onLine,
      throughputMs: null, throughputBytes: null,
    });
  }

  async function start() {
    reset();
    setRunning(true);

    // Connection API
    const nav = navigator as Navigator & { connection?: NetworkInformation };
    const conn = nav.connection;
    if (conn) {
      setMetrics((m) => ({
        ...m,
        connectionApi: true,
        effectiveType: conn.effectiveType || '—',
        downlink: conn.downlink ?? null,
        rtt: conn.rtt ?? null,
      }));
    }

    // Simulated throughput test: generate data and measure JSON processing speed
    const testSize = 2 * 1024 * 1024; // 2MB of data
    const data = new Array(10000);
    for (let i = 0; i < 10000; i++) {
      data[i] = { id: i, payload: 'x'.repeat(200), ts: Date.now() };
    }
    const t0 = now();
    const json = JSON.stringify(data);
    JSON.parse(json);
    const elapsed = now() - t0;
    const bytes = json.length * 2;

    setMetrics((m) => ({ ...m, throughputMs: elapsed, throughputBytes: bytes }));
  }

  function stop() {
    setRunning(false);
    let sc = 40;
    if (metrics.connectionApi) sc += 15;
    if (metrics.online) sc += 10;
    if (metrics.effectiveType === '4g') sc += 15;
    else if (metrics.effectiveType === '3g') sc += 5;
    if (metrics.throughputMs != null && metrics.throughputMs < 200) sc += 20;
    else if (metrics.throughputMs != null && metrics.throughputMs < 500) sc += 10;
    sc = Math.min(100, sc);

    const testId: TestId = 'mobile-network';
    reportResult(testId, {
      score: sc,
      primary: `${metrics.effectiveType} · ${metrics.online ? 'Online' : 'Offline'}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  // Online/offline listener
  useEffect(() => {
    if (!running) return;
    function onOnline() { setMetrics((m) => ({ ...m, online: true })); }
    function onOffline() { setMetrics((m) => ({ ...m, online: false })); }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [running]);

  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => stop(), 3000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  return (
    <TestCard title={t('mobile.network.title', language)} description={t('mobile.network.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('netcap.type', language)} value={metrics.effectiveType} hint={metrics.connectionApi ? 'Connection API' : 'No API'} />
        <MetricBox label={t('netcap.downlink', language)} value={metrics.downlink != null ? String(metrics.downlink) : '—'} />
        <MetricBox label={t('netcap.rtt', language)} value={metrics.rtt != null ? String(metrics.rtt) : '—'} />
        <MetricBox label={t('netcap.offline', language)} value={metrics.online ? 'Online' : 'Offline'} />
      </div>
      <div className="grid grid-cols-2 gap-2.5 mt-2.5 max-sm:grid-cols-1">
        <MetricBox label={t('netcap.throughput', language)} value={fmtMs(metrics.throughputMs)} hint="Simulated 2MB JSON" />
        <MetricBox label="Payload size" value={fmtBytes(metrics.throughputBytes)} />
      </div>
    </TestCard>
  );
}
